import { CheckCircle2, XCircle } from "lucide-react";
import { formatMoney } from "@/lib/format";
import IssueList from "@/components/dq/IssueList";

const Mark = ({ ok, testId }) => ok
  ? <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" data-testid={testId} aria-label="matches" />
  : <XCircle className="h-4 w-4 text-rose-400 inline" data-testid={testId} aria-label="differs" />;

const Th = ({ children, right }) => <th className={`px-4 py-2.5 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, mono, right, muted }) => <td className={`px-4 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${right ? "text-right" : ""} ${muted ? "text-muted-foreground" : ""}`}>{children}</td>;
const Box = ({ title, children, testId }) => (
  <section className="mt-8" data-testid={testId}>
    <h2 className="font-display text-base md:text-lg font-semibold tracking-tight mb-3">{title}</h2>
    <div className="border border-border bg-card overflow-x-auto">{children}</div>
  </section>
);

const YearsTable = ({ years }) => (
  <table className="w-full text-sm">
    <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
      <Th>FY</Th><Th right>Rows (expected)</Th><Th right>Received</Th><Th right>Workbook income</Th><Th right>Spent</Th>
      <Th right>Workbook expenditure</Th><Th right>Net</Th><Th right>Workbook net</Th><Th>Match</Th>
    </tr></thead>
    <tbody>
      {years.map((y) => (
        <tr key={y.fy} className="border-b border-border" data-testid={`preview-year-${y.fy}`}>
          <Td mono>{y.fy}</Td>
          <Td mono right>{y.rows}{y.skipped.length ? ` +${y.skipped.length} skipped` : ""} <span className="text-muted-foreground">({y.expected_rows ?? "—"})</span></Td>
          <Td mono right>{formatMoney(y.received)}</Td><Td mono right muted>{formatMoney(y.workbook?.income)}</Td>
          <Td mono right>{formatMoney(y.spent)}</Td><Td mono right muted>{formatMoney(y.workbook?.expenditure)}</Td>
          <Td mono right>{formatMoney(y.net)}</Td><Td mono right muted>{formatMoney(y.workbook?.net)}</Td>
          <Td><Mark ok={y.match} testId={`preview-year-match-${y.fy}`} /></Td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CategoryTable = ({ years }) => (
  <table className="w-full text-sm">
    <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
      <Th>FY</Th><Th>Category</Th><Th right>App</Th><Th right>Workbook</Th><Th>Match</Th>
    </tr></thead>
    <tbody>
      {years.flatMap((y) => y.categories.map((c) => (
        <tr key={`${y.fy}-${c.name}`} className="border-b border-border align-top" data-testid={`preview-category-${y.fy}-${c.name}`}>
          <Td mono>{y.fy}</Td>
          <Td>{c.name}{c.helper_rows.length > 0 && (
            <div className="text-xs text-amber-300 mt-1">Workbook Support Column is wrong on: {c.helper_rows.join("; ")}</div>
          )}</Td>
          <Td mono right>{formatMoney(c.app)}</Td><Td mono right muted>{formatMoney(c.workbook)}</Td><Td><Mark ok={c.match} /></Td>
        </tr>
      )))}
    </tbody>
  </table>
);

const PersonalTable = ({ p }) => (
  <table className="w-full text-sm">
    <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
      <Th>Director</Th><Th right>Debits</Th><Th right>Workbook</Th><Th right>Credits</Th><Th right>Workbook</Th><Th>Match</Th>
    </tr></thead>
    <tbody>
      {p.directors.map((d) => (
        <tr key={d.name} className="border-b border-border" data-testid={`preview-personal-${d.name}`}>
          <Td>{d.name}</Td><Td mono right>{formatMoney(d.debits)}</Td><Td mono right muted>{formatMoney(d.wb_debits)}</Td>
          <Td mono right>{formatMoney(d.credits)}</Td><Td mono right muted>{formatMoney(d.wb_credits)}</Td><Td><Mark ok={d.match} /></Td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Skipped = ({ list }) => (
  <ul className="text-sm divide-y divide-border">
    {list.map((s) => <li key={s.ref} className="px-4 py-2"><span className="font-mono text-xs text-muted-foreground mr-3">{s.ref}</span>{s.description || "(no description)"} — {s.reason}</li>)}
  </ul>
);

export default function ImportPreview({ preview }) {
  const p = preview.personal;
  const skipped = [...preview.years.flatMap((y) => y.skipped), ...p.skipped];
  return (
    <div data-testid="import-preview">
      <Box title="Monthly sheets: app totals next to the workbook's totals" testId="preview-years"><YearsTable years={preview.years} /></Box>
      <Box title="Category summary (years the workbook categorises)" testId="preview-categories">
        <CategoryTable years={preview.years.filter((y) => y.categories.length && y.categories.some((c) => Number(c.workbook) !== 0))} />
      </Box>
      <Box title={`Personal ledger: ${p.rows} rows (expected ${p.expected_rows}) → ${p.entries} director entries`} testId="preview-personal"><PersonalTable p={p} /></Box>
      <div className="mt-6 grid gap-3 grid-cols-2 md:grid-cols-4 text-sm" data-testid="preview-other">
        <div className="border border-border bg-card p-4">Adjustments<div className="font-mono mt-1">{preview.adjustments.map((a) => `${a.director} ${formatMoney(a.amount)}`).join(" · ")}</div></div>
        <div className="border border-border bg-card p-4">Misc balances<div className="font-mono mt-1">{preview.misc.count} lines · {formatMoney(preview.misc.total)}</div></div>
        <div className="border border-border bg-card p-4">Memo notes<div className="font-mono mt-1">{preview.memos}</div></div>
        <div className="border border-border bg-card p-4">Forecast items<div className="font-mono mt-1">{preview.forecasts}</div></div>
      </div>
      {skipped.length > 0 && <Box title={`Rows that won't be imported (${skipped.length})`} testId="preview-skipped"><Skipped list={skipped} /></Box>}
      <section className="mt-10">
        <h2 className="font-display text-base md:text-lg font-semibold tracking-tight mb-1">Data-quality checks on this file</h2>
        <p className="text-sm text-muted-foreground mb-4">Nothing is changed now. After importing, fix these one by one in Data quality.</p>
        <IssueList checks={preview.checks} readOnly />
      </section>
    </div>
  );
}
